import { useState } from "react";
import {
  BaseOmittedFields,
  FilterFieldBase,
  FilterType,
  useFilterBase,
} from "./FilterBase";
import { Box, Button, IconButton, Popover, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { MostRecentOption, useMostRecentOptions } from "../../hooks/useMostRecentOptions";
import { v1 } from "uuid";
import { PopoverTriggerButton, StyledPopoverBody } from "./FieldPopover";
import { IoCloseOutline } from "react-icons/io5";
import { TextOverflow } from "../TextOverflow";

export type InputFilter = FilterFieldBase<string> & {
  type: FilterType.INPUT;
  main?: boolean;
  mostRecentOptionsKey?: string;
};

export const useInputFilter = (
  params: Omit<InputFilter, BaseOmittedFields>
): InputFilter => {
  const filterBase = useFilterBase(params, "");

  return {
    ...params,
    ...filterBase,
    type: FilterType.INPUT,
  };
};

const useInputFilterHook = (props: InputFilter) => {
  const { options: mostRecentOptions, addOption, removeOption } = useMostRecentOptions(props.mostRecentOptionsKey || v1());

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  /// We don't use localValue, setLocalValue which is pass down from the props,
  /// because of the performance issue. We don't want to rerender the whole component (popover, input field...)
  /// when we change the localValue. We want to rerender only the input field.
  const [localValue, setLocalValue] = useState<string>("");

  const currentValue: string = props.getValue();

  const openPopover = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(null);
    props.setLocalValue(currentValue);
  };

  const open = Boolean(anchorEl);

  const applyFilter = (newValue?: string) => {
    const valueToApply = newValue || localValue;
    props.onChange(valueToApply);
    if (props.mostRecentOptionsKey && valueToApply) addOption(valueToApply);
    setAnchorEl(null);
    setLocalValue("");
  };

  const resetFilter = () => {
    setLocalValue("");
    props.onChange("");
    setAnchorEl(null);
  };

  return {
    anchorEl,
    openPopover,
    closePopover,
    open,
    applyFilter,
    currentValue,
    resetFilter,
    localValue,
    removeOption,
    setLocalValue,
    mostRecentOptions,
  };
}

export const InputFilterField = (props: InputFilter) => {
  const theme = useTheme();

  const onlySmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  if (onlySmallScreen) {
    return <MobileInputFilterField {...props} />;
  }

  return <DesktopInputFilterField {...props} />;
}

export const DesktopInputFilterField = (props: InputFilter) => {
  const theme = useTheme();

  const { placeholder, outlined } = props;

  const {
    openPopover,
    currentValue,
    anchorEl,
    closePopover,
    localValue,
    setLocalValue,
    applyFilter,
    resetFilter,
    removeOption,
    open,
    mostRecentOptions,
  } = useInputFilterHook(props);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      applyFilter();
    }
  }

  return <>
    <PopoverTriggerButton
      onClick={openPopover}
      placeholder={placeholder}
      value={currentValue}
      outlined={outlined}
    />

    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={closePopover}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <Box sx={{ minWidth: "300px" }}>
        <Box
          sx={{
            width: "100%",
            padding: theme.spacing(1),
            borderBottom: `1px solid ${theme.palette.divider}`,
            paddingY: 2,
            paddingX: 2,
          }}
        >
          <Typography variant="subtitle2">
            <strong>{placeholder}</strong>
          </Typography>
        </Box>

        <StyledPopoverBody>
          <TextField
            label={placeholder}
            variant="filled"
            size="small"
            fullWidth
            value={localValue}
            onChange={(event) => setLocalValue(event.target.value)}
            onKeyDown={handleKeyDown}
          />

          {props.mostRecentOptionsKey && mostRecentOptions.length > 0 &&
            <MostRecentOptions
              options={mostRecentOptions}
              onSelect={(value: string) => applyFilter(value)}
              removeOption={removeOption}
              localValue={localValue}
            />
          }
        </StyledPopoverBody>

        <Box padding={1} display="flex" justifyContent="flex-end" gap={1}>
          <Button onClick={() => applyFilter()} size="small">
            Použiť
          </Button>

          <Button onClick={resetFilter} size="small" color="secondary">
            Vymazať
          </Button>
        </Box>
      </Box>
    </Popover>
  </>;
}

export const MobileInputFilterField = (props: InputFilter) => {
  const { placeholder, localValue, setLocalValue } = props;

  const { applyFilter } = useInputFilterHook(props);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      applyFilter();
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: (theme) => theme.spacing(2),
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 1,
      }}>
        <Typography variant="subtitle2">
          <strong>{placeholder}</strong>
        </Typography>
      </Box>

      <TextField
        label={placeholder}
        variant="filled"
        size="small"
        fullWidth
        value={localValue}
        onChange={(event) => setLocalValue(event.target.value)}
        onKeyDown={handleKeyDown}
      />
    </Box>
  );
}

type MostRecentOptionsProps = {
  options: MostRecentOption[];
  onSelect: (value: string) => void;
  removeOption: (value: string) => void;
  localValue: string;
}

const MostRecentOptions = ({ options, onSelect, localValue, removeOption }: MostRecentOptionsProps) => {
  const theme = useTheme();

  const filteredOptions = options.filter((option) => {
    if (!localValue) return options;
    return option.value.toLowerCase().includes(localValue.toLowerCase());
  });

  if (!filteredOptions.length) return <></>

  return (
    <Box sx={{ display: "flex", flexDirection: "column", marginTop: 2 }}>
      <Typography paddingX={1} variant="body2">
        Najčastejšie vyhľadávané
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {
          filteredOptions.map((option, index) => (
            <Box
              onClick={() => onSelect(option.value)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transition: "background-color 0.2s",
                cursor: "pointer",
                paddingX: 1,
                paddingY: 0.5,
                borderRadius: `${theme.shape.borderRadius}px`,
                "&:hover": { backgroundColor: theme.palette.grey[100] },
              }}
              key={index + 1}
            >
              <TextOverflow
                variant="body1"
                color={theme.palette.text.primary}
                maxWidth={280}
                text={option.value}
              />

              <Box onClick={(e) => e.stopPropagation()}>
                <IconButton
                  sx={{ padding: 0 }}
                  onClick={(event) => {
                    event.stopPropagation();
                    removeOption(option.value);
                  }}
                >
                  <IoCloseOutline />
                </IconButton>
              </Box>
            </Box>
          ))
        }
      </Box>
    </Box>
  );
}